import { Item } from "../../../pojo/Item";
import { EventCopyOccurrencePayload } from "../../../pojo/ReservationI";
import { S25Util } from "../../../util/s25-util";
import { StateService } from "../../../services/state.service";
import { OlsService } from "../../../services/ols.service";
import { EventService } from "../../../services/event.service";
import { S25Assert } from "../../../util/s25.assert";
import { Event } from "../../../pojo/Event";
import { AccessLevels } from "../../../pojo/Fls";
import { Fls } from "../../../pojo/Fls";

interface RoseParams {
    clone_id?: number;
    event_id?: number;
    preload_ids?: number[];
}

const Unspecified = -1;
type Unspecified = typeof Unspecified;

/**
 * UI messages for event relationships
 */
export const RelationshipMessages = {
    inSet: "is in a related event set",
    notInSet: "is not in a related event set",
} as const;

export class RoseUtil {
    /**
     * Error message shown when user lacks sufficient privileges to alter events in a set.
     * Public so it can be used by modals and components.
     */
    public static readonly insufficientPrivilegesMsg =
        "You do not have sufficient privileges to alter all of the events in this set. Please contact your administrator.";

    /**
     * Computes the allowed event states based on FLS permissions and action type.
     * This logic is shared between copy/edit modals and more actions.
     *
     * @param type - "edit" or "clone"
     * @param allowedStates - The allowed states from user preferences
     * @param fls - The FLS permissions object
     * @returns Array of allowed state IDs, or [Unspecified] (-1) if none/undefined
     */
    public static computeAllowedStates(
        type: "edit" | "clone",
        allowedStates: number[] | undefined,
        fls: Fls | undefined,
    ): Event.State.Id[] | [Unspecified] {
        if (!allowedStates || !fls) return [Unspecified];

        const states = new Set<Event.State.Id>();

        if (type === "edit") {
            if ([AccessLevels.Full, AccessLevels.Edit].includes(fls.EVENT_EVS)) {
                allowedStates.forEach((state) => states.add(state));
            }
            if ([AccessLevels.Full, AccessLevels.Edit].includes(fls.EVENT_DRAFT)) {
                states.add(Event.State.Ids.Draft);
            } else {
                states.delete(Event.State.Ids.Draft);
            }
        } else {
            // clone
            if (fls.EVENT_DRAFT === AccessLevels.Full) {
                states.add(Event.State.Ids.Draft);
            }
            if (fls.EVENT_EVS === AccessLevels.Full) {
                states.add(Event.State.Ids.Tentative);
                states.add(Event.State.Ids.Confirmed);
                states.add(Event.State.Ids.Sealed);
                states.add(Event.State.Ids.Denied);
                states.add(Event.State.Ids.Cancelled);
            }
        }

        return states.size ? Array.from(states) : [Unspecified];
    }

    public static openEventInRose(
        processType: "clone" | "edit",
        eventId: number,
        onProcessFinished?: () => void,
        occurrencePayload?: EventCopyOccurrencePayload,
    ) {
        return RoseUtil.processSelected(
            processType,
            false,
            [eventId],
            occurrencePayload,
            false,
            [],
            () => {},
            onProcessFinished,
        );
    }

    public static copyAndRelateBack(
        eventId: number,
        occurrencePayload?: EventCopyOccurrencePayload,
        onProcessFinished?: () => void,
    ) {
        return RoseUtil.processSelected(
            "clone",
            true,
            [eventId],
            occurrencePayload,
            false,
            [],
            function (objIdArray: number[]) {
                objIdArray.push(eventId);
                //relate real event to the new event(s)
                if (objIdArray.length > 1) {
                    EventService.relateEvents(objIdArray, eventId, 1);
                }
            },
            onProcessFinished,
        );
    }

    public static copyAndRelateSelected(
        eventIdArr: number[],
        occurrencePayload?: EventCopyOccurrencePayload,
        onProcessFinished?: () => void,
    ) {
        return RoseUtil.processSelected(
            "clone",
            true,
            eventIdArr,
            occurrencePayload,
            true,
            [],
            function (objIdArray: number[]) {
                //only relate if mult events, this relates all new event ids together (we cleanup orphans daily)
                if (objIdArray.length > 1) {
                    EventService.preSetEventRelationships(objIdArray);
                }
            },
            onProcessFinished,
        );
    }

    public static editSelected(eventIdArr: number[], onProcessFinished?: () => void) {
        return RoseUtil.processSelected("edit", false, eventIdArr, undefined, false, [], () => {}, onProcessFinished);
    }

    /**
     * Process user selection; if copying and relating back or copying and relating set,
     * wait for tabs to load and relate new ids
     */
    public static async processSelected(
        processType: "clone" | "edit",
        relateSelected: boolean,
        selectedIds: number[],
        occurrencePayload: EventCopyOccurrencePayload | undefined,
        hasPreRelate: boolean,
        objIdArray: number[],
        onRoseFinished?: (objIdArray: number[]) => void,
        onProcessFinished?: () => void,
    ) {
        const roseParams: RoseParams = {};
        if (!selectedIds?.length) return;
        const objects = S25Util.array.unique(
            selectedIds.map((id) => S25Util.toInt(id)).filter((id) => Number.isFinite(id)),
        );
        if (!objects.length) return;

        const primarySegment = occurrencePayload?.segments?.[0];
        const dayOffset = occurrencePayload?.dayOffset || 0;
        const eventDayOffsets = occurrencePayload?.eventDayOffsets;
        const dayOffsetForEvent = (eventId: number | string | undefined): number => {
            const numericId = S25Util.toInt(eventId);
            if (Number.isFinite(numericId) && eventDayOffsets && eventDayOffsets[numericId] != null) {
                return eventDayOffsets[numericId];
            }
            return dayOffset;
        };
        switch (processType) {
            case "clone":
                if (!occurrencePayload?.segments?.length) {
                    roseParams.clone_id = objects[0];
                    roseParams.preload_ids = objects.slice(1);
                } else {
                    const mappedEventId = S25Util.toInt(primarySegment?.occurrences[0]?.eventId) || objects[0];
                    roseParams.clone_id = mappedEventId;
                    roseParams.preload_ids = objects.filter((eventId) => eventId !== mappedEventId);
                }
                break;
            case "edit":
                roseParams.event_id = objects[0];
                roseParams.preload_ids = objects.slice(1);
                break;
            default:
                S25Assert.never(processType, "Unhandled processType in RoseUtil.processSelected");
        }

        //check EDIT/OWN OLS for event and selected events
        const itemIdArr = [...(roseParams.preload_ids || []), (roseParams.clone_id || roseParams.event_id)!];
        const levels: string[] = ["F"]; //if F, we are good
        if (processType === "edit") {
            levels.push("C"); //for edit mode, if C, we are good
        }

        //check if each event has either EDIT=F, EDIT=C, OR OWN=F
        const hasOlsPerms = await OlsService.getOlsMultipleAllAtLeast(
            itemIdArr,
            Item.Ids.Event,
            ["edit", "own"],
            ["edit", { perm: "own", level: "F" }] as any,
            levels as any,
        );

        if (!hasOlsPerms) {
            alert(RoseUtil.insufficientPrivilegesMsg);
            return;
        }

        const roseItem = {
            itemId: roseParams.clone_id || roseParams.event_id,
            itemTypeId: Item.Ids.Event,
            isCopy: processType === "clone",
            relatedIds: roseParams.preload_ids,
            occurrencePayload: occurrencePayload,
            occurrences: primarySegment?.occurrences,
            profileCode: primarySegment?.profileCode ?? primarySegment?.occurrences?.[0]?.profile_code,
            dayOffset: dayOffsetForEvent(roseParams.clone_id || roseParams.event_id) || undefined,
            eventDayOffsets,
        };

        if ((processType === "clone" && !relateSelected) || processType === "edit") {
            //if clone and NOT relate selected, or edit mode
            try {
                StateService.gotoRose(roseItem);
            } catch (error) {
                console.log(roseParams, error);
            }
        } else if (processType === "clone" && relateSelected) {
            //if clone and relate selected
            let hasRelatePerms: boolean = true;
            if (!hasPreRelate) {
                //if not a pre-relate call, we should check relate perms to see if we can relate to this event and its set
                const relatePermsData = await EventService.relatePerms(itemIdArr);
                hasRelatePerms = !(relatePermsData && relatePermsData.data && parseInt(relatePermsData.data.count) > 0);
            }

            if (!hasRelatePerms) {
                alert(RoseUtil.insufficientPrivilegesMsg);
                return;
            }

            try {
                //call rose to create new copy tabs (creates new event ids)
                const eventIds = (await StateService.gotoRose(roseItem)) as number | number[];
                const eventIdsArray = S25Util.array.forceArray(eventIds);
                objIdArray.push(...eventIdsArray);
                const mappedObjIdArray = objIdArray.map(S25Util.toInt);
                onRoseFinished?.(mappedObjIdArray); //send obj id array to onRoseFinished method
            } catch (e) {
                console.error(roseParams, e);
            }
        }
        onProcessFinished?.();
    }
}
